import { useState ,useEffect,useRef} from "react";
import {
  GoogleMap,
  Marker,
  useJsApiLoader,
} from "@react-google-maps/api";
import { useRecoilState } from "recoil"
import { CurrentPosition ,CurrentMaker, RelevantMarkers, Bookmarks, UaParser, CurrentPoint, CurrentAmount, CurrentEmission} from "../store/currentState";
import OverviewCard from "../component/OverviewCard";
import AlertsSnackbar from "../component/AlertsSnackbar";
import BottomDrawer from "../component/BottomDrawer";
import LoadingBox from "../component/LoadingBox";
import { MakerData, Position } from "../types/type";
import { getCurrentPosition, getDistance, isIncludes, sortMarkersAtCenter} from "../hooks/useFunction";
import { Box, Fab, Backdrop, CircularProgress} from "@mui/material";
import NearMeIcon from '@mui/icons-material/NearMe';
import ClearIcon from '@mui/icons-material/Clear';
import Search from "@mui/icons-material/Search";
import FormControl from '@mui/material/FormControl';
import MapMultiSelect from "../component/MapMultiSelect";
import AlertsDialog from "../component/AlertsDialog";
import { useNavigate } from "react-router-dom";
import { UAParser } from "ua-parser-js";
import { getPlacesApi, getBookmarksApi, getCategoriesApi, getMyPlacesApi, getPlacePointsApi, getPlaceStatisticsApi } from "../services/api/api";
import { mapDistance, permittedRadius } from "../constants/constants";
import { useCookies } from "react-cookie";
import MyBottomNavigation from "../component/MyBottomNavigation";
import { useTranslation } from "react-i18next";

// type Libraries = ("drawing" | "geometry" | "localContext" | "places" | "visualization")[]; // ライブラリの方を関数の外側で行わないとlibrariesを使う時performance warningが表示される表示のみで実際にパフォーマンスは低下しない（参考：https://github.com/JustFly1984/react-google-maps-api/issues/238）
type Libraries = ("drawing" | "geometry" | "places" | "visualization")[]; // localContext を削除
const libraries:Libraries = ['places'];

function Map(): JSX.Element {
  const { t } = useTranslation();
  const [cookies, setCookie, removeCookie] = useCookies(['recotech_mapapps_token']); //クッキーの取得
  const [isWaitingResponse, setIsWaitingResponse] = useState<boolean>(false);

  const bounds = useRef<google.maps.LatLngBounds | null>(null);
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY!,
    libraries,
  });
  const navigate = useNavigate();
  const [isCenterLoaded, setIsCenterLoaded] = useState<boolean>(false);
  const [tapTime, setTapTime] = useState<number>(0);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [zoom, setZoom] = useState<number>(18);
  const [categoryNames, setCategoryNames] = useState<string[]>([]);
  const [currentPosition, setCurrentPosition] = useRecoilState(CurrentPosition);
  const [initialPosition, setInitialPosition] = useState<Position>({lat:0,lng:0});
  const [marker, setMarker] = useRecoilState(CurrentMaker);
  const [markers, setMarkers] = useState<MakerData[]>([]);
  const [initialMarkers, setInitialMarkers] = useState<MakerData[]>([]);
  const [relevantMarkers, setRelevantMarkers] = useRecoilState(RelevantMarkers);
  const [bookmarks, setBookmarks] = useRecoilState(Bookmarks);
  const [categoryNameAll, setCategoryNameAll] = useState<string[]>([]);
  const [point, setPoint] = useRecoilState(CurrentPoint);
  const [amount, setAmount] = useRecoilState(CurrentAmount);
  const [emission, setEmission] = useRecoilState(CurrentEmission);
  const [myPlaces, setMyPlaces] = useState<number[]>([]);

  const [registerPosition, setRegisterPosition] = useState<Position | null>(null);
  const [registerPlaceId, setRegisterPlaceId] = useState<string | null>(null);

  //BottomDrawerの設定
  const [openBottomDrawer, setOpenBottomDrawer] = useState<boolean>(false);
  const bottomDrawerRef = useRef<HTMLDivElement>(null);

  //SnackBarの設定
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [alertsMessage, setAlertsMessage] = useState<string>("");
  const [severity, setSeverity] = useState< "error" | "success" >("error");

  //Selectの設定
  const [openSelect, setOpenSelect] = useState<boolean>(false);

  //UAの情報
  const [uaParser, setUaParser] = useRecoilState(UaParser);

  //GPSチェック
  const [isGpsOn, setIsGpsOn] = useState<boolean>(true);

  useEffect(() => {
    const uaParserResult = UAParser(window.navigator.userAgent);
    setUaParser(uaParserResult)
    if(
      uaParserResult.browser.name === 'Chrome' || 
      uaParserResult.browser.name === 'Safari' || 
      uaParserResult.browser.name === 'Mobile Safari'
    ){
    }
    else{
      setAlertsMessage(t("pages.map.browserUnsupported", { browserName: uaParserResult.browser.name }))
      setSeverity("error")
      setOpenSnackbar(true)
    }
    setTimeout(() => {
      if(!isLoaded || !isCenterLoaded)
      setIsGpsOn(false)
    }, 5000); //5秒経ってもマップが表示できなければGPSがOFFである可能性にダイアログを開く
  }, []);

  const onLoad = (map: google.maps.Map) => {
    setMap(map);
  };

  const onUnmount = () => {
    setMap(null);
  };


  const updatePosition = (position:any) => {
    const lat = position.coords.latitude;
    const lng = position.coords.longitude;
    setCurrentPosition({ lat, lng });
  };
  
  const handleError = (error:any) => {
    console.error(t("pages.map.locationError"), error);
    // ここで適切なエラー処理を行う
  };
  
  useEffect(() => {
    let watchId:any;
  
    if (navigator.geolocation) {
      watchId = navigator.geolocation.watchPosition(updatePosition, handleError, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      });
    }
  
    return () => {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, []);

  //Mapをタップした時の処理
  const handleMapClick = (event: google.maps.MapMouseEvent) => {
    setMarkers((currentMarkers) =>
      // 現在の状態(currentMarkers)に変更を加えて、新しいオブジェクト( {...marker} )の配列にマップ
      currentMarkers.map((marker, index) => {
        if (marker.id !== null && myPlaces.includes(marker.id)) {
          return { ...marker, iconUrl: './assets/pin_nomal_owner.png' };
        }
        return { ...marker, iconUrl: './assets/pin_nomal.png' };
      })
    );
    setMarker(null)
    map!.panTo(event.latLng!);
    if(cookies.recotech_mapapps_token){
      if ("placeId" in event) {
        setRegisterPlaceId(null) 
        setRegisterPlaceId(event.placeId  as string)
        setTimeout(() => setOpenEditDialog(true), 500); //0.5秒後にダイアログを開く
      }  
    }
    setOpenSelect(false)
  };

  //Mapをロングタップ開始した時
  const handleMapMouseDown = (event: google.maps.MapMouseEvent) => {
    setTapTime(new Date().getTime())
  };

  //Mapをロングタップ終了した時
  const handleMapMouseUp = (event: google.maps.MapMouseEvent) => {
    if(cookies.recotech_mapapps_token && tapTime > 0 && new Date().getTime() - tapTime > 1000){ //ログインし、1秒間以上タップしていたら
      setRegisterPlaceId(null) //handleEditAgreeClickで判別するために必要
      setRegisterPosition({
        lat: event.latLng!.lat(),
        lng: event.latLng!.lng(),
      });  
      map!.panTo(event.latLng!);
    }
    setTapTime(0)
  };
  
  const [openCheckinDialog, setOpenCheckinDialog] = useState<boolean>(false);
  const handleCheckinAgreeClick = (async () => {
    setOpenCheckinDialog(false)
    navigate('/checkin')
  })

  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const handleEditAgreeClick = (async () => {
    if(registerPlaceId){ // 施設をタップで登録した場合
      const placesService = new google.maps.places.PlacesService(map!);
      placesService.getDetails(
        { placeId: registerPlaceId as string },
        (place, status) => {

          let openingHours = ""
          let notOpeningHours = ""

          if(place?.opening_hours){
            place.opening_hours.weekday_text!.forEach((element) => {
              if(element.includes(t("pages.map.closedDays"))){
                notOpeningHours = notOpeningHours + element.slice(0,2) + "、"
              }else{
                openingHours = openingHours + element + "\n"
              }
            });
            if(notOpeningHours !== "") notOpeningHours = notOpeningHours.slice(0,-1)
            if(openingHours !== "") openingHours = openingHours.slice(0,-1)
          }

          if (status === "OK" && place) {
            navigate('/addplace',{state: {
              id: null,
              latitude: place.geometry?.location?.lat(),
              longitude: place.geometry?.location?.lng(),
              name: place.name,
              imageUrl: null,
              iconUrl: null,
              categoryNames: [],
              zipCode: place.address_components?.slice(-1)[0]?.long_name ?? "",
              address: place.formatted_address?.substr(13) ?? "",
              phoneNumber: place.formatted_phone_number ?? "",
              description: null,
              regularHolidays: notOpeningHours,
              businessHours: openingHours,
            }})    
          }
        }
      );
      
    }else{ // ロングタップで登録した場合
      const geocoder = new google.maps.Geocoder();
      try {
        const response = await geocoder.geocode({ location: {lat:registerPosition!.lat,lng:registerPosition!.lng} });
        let address = null;
        let zipCode = null;
        if (response?.results?.[0]?.formatted_address) {
          address = response.results[0].formatted_address.substr(13)
          zipCode = response.results[0].address_components?.slice(-1)[0]?.long_name
        }
        navigate('/addplace',{state: {
          id: null,
          latitude: registerPosition!.lat,
          longitude: registerPosition!.lng,
          name: null,
          imageUrl: null,
          iconUrl: null,
          categoryNames: [],
          zipCode: zipCode,
          address: address,
          phoneNumber: null,
          description: null,
          regularHolidays: null,
          businessHours: null,    
        }})
      } catch (error) {
      }
    }
    setOpenEditDialog(false)
  })

  //TemporaryMarkerをクリックした時の処理
  const handleTemporaryMarkerClick = (() => {
    setOpenEditDialog(true)
  });
  
  //Markerをクリックした時の処理
  const handleMarkerClick = (async (clickedMarker:MakerData, clickedIndex:number) => {
    setMarkers((currentMarkers) =>
      // 現在の状態(currentMarkers)に変更を加えて、新しいオブジェクト( {...marker} )の配列にマップ
      currentMarkers.map((marker, index) => {
        if (index === clickedIndex) {
          // クリックされたマーカーだけ、`iconUrl`を上書き
          return { ...clickedMarker, iconUrl: './assets/pin_check.png' }
        } else if (marker.id !== null && myPlaces.includes(marker.id)) {
          return { ...marker, iconUrl: './assets/pin_nomal_owner.png' };
        } else {
          return { ...marker, iconUrl: './assets/pin_nomal.png' }
        }
      })
    );
    setMarker(clickedMarker);
    map!.panTo({lat:clickedMarker.latitude,lng:clickedMarker.longitude});

    //ログイン時clickedMarkerのlatitude,longitudeと現在地の距離を計算しpermittedRadiusより小さければダイアログを開く
    if(cookies.recotech_mapapps_token){
      navigator.geolocation.getCurrentPosition((position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setCurrentPosition({ lat, lng });      
      });
      const distance = getDistance(clickedMarker.latitude,clickedMarker.longitude,currentPosition.lat,currentPosition.lng);
      if(distance < permittedRadius){
        setTimeout(() => setOpenCheckinDialog(true), 500); //0.5秒後にダイアログを開く
      }
    }

  });
  
  //Fabをクリックした時の処理
  const handleFabClick = (() => {
    map?.setZoom(18);
    getCurrentPositionAndMoveCenter()
  });
    
  //Searchアイコンをクリックした時の処理
  const handleSearchClick = ((categoryNames:string[],initialMarkers:MakerData[]) => {
    //検索条件にマッチしたマーカーを取得
    const searchMarkerData = initialMarkers.filter( function( marker ) {
      return isIncludes(categoryNames,marker.categoryNames) ;
    })
    setMarker(null)
    setMarkers([])
    searchMarkerData.map((marker) => {
      setMarkers((currentMarkers) => [
        ...currentMarkers,{...marker}, //useRecoilStateで取得したobjctはreadonlyになっているため、スプレッド構文で新規に作り直す
      ]);
    });
    if(searchMarkerData.length === 0){ 
      setAlertsMessage(t("pages.map.noPointsFound"))
      setSeverity("error")
      setOpenSnackbar(true)
    }else{
      panToAnyMarker(searchMarkerData,map) //検索結果があれば全てのマーカーを表示
    }
  });
  
  //クリアアイコンをクリックした時の処理
  const handleClearClick = ((initialMarkers:MakerData[]) => {    
    setMarker(null)
    setMarkers([])

    initialMarkers.map((marker) => {
      setMarkers((currentMarkers) => [
        ...currentMarkers,{...marker}, //useRecoilStateで取得したobjctはreadonlyになっているため、スプレッド構文で新規に作り直す
      ]);
    });
    panToAnyMarker(initialMarkers,map)
    setCategoryNames([])
  });

  //戻るをクリックした時の処理
  const handleBackClick = (() => {
    let  checkedMarkers = markers
    checkedMarkers.map((marker) => {
      marker.iconUrl = './assets/pin_nomal.png'
    });
    setMarker(null)
    setMarkers([])
    checkedMarkers.map((marker) => {
      setMarkers((currentMarkers) => [
        ...currentMarkers,
        {
          ...marker,
          iconUrl: marker.id !== null && myPlaces.includes(marker.id) ? './assets/pin_nomal_owner.png' : './assets/pin_nomal.png'
        },
      ]);
    });
    setOpenBottomDrawer(false)
  })

  //現在の緯度軽度を取得しセンターとしてセットする
  const getCurrentPositionAndMoveCenter = () => {
    navigator.geolocation.getCurrentPosition((position) => {
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      setInitialPosition({ lat, lng });
      setCurrentPosition({ lat, lng });      
      setIsCenterLoaded(true);
    });
  };

  //api情報を読み込みセットする
  const fetchData = async () => {
    setMarker(null)
    setMarkers([])
    setInitialMarkers([])

    // 位置情報から国を判別する
    let country_id = 1; // デフォルトは1（マレーシア以外）
    const geocoder = new google.maps.Geocoder();
    try {
      const response = await geocoder.geocode({ location: {lat: currentPosition.lat, lng: currentPosition.lng} });
      if (response.results && response.results.length > 0) {
        // 住所コンポーネントから国を取得
        const addressComponents = response.results[0].address_components;
        for (let i = 0; i < addressComponents.length; i++) {
          const component = addressComponents[i];
          if (component.types.includes('country')) {
            const countryCode = component.short_name;
            // マレーシアかどうかをチェック
            if (countryCode === 'MY') {
              country_id = 2;
            }
            break;
          }
        }
      }
    } catch (error) {
      console.error("Geocoder error:", error);
    }

    //カテゴリー情報を取得
    const response = await getCategoriesApi(setAlertsMessage, setSeverity, setOpenSnackbar, country_id);
    const categoryNameAll = response.map((category) => { return category.name })
    setCategoryNameAll(categoryNameAll)

    // マーカー情報を取得
    let placesBounds = await getPlacesApi(100, currentPosition!.lat, currentPosition!.lng, mapDistance, setAlertsMessage, setSeverity, setOpenSnackbar);

    if (cookies.recotech_mapapps_token) {
      // ブックマーク情報を取得
      const userBookmarks = await getBookmarksApi(cookies.recotech_mapapps_token, removeCookie);
      setBookmarks(userBookmarks);

      // マーカー情報にMY回収場所情報を付与
      const myPlaces = await getMyPlacesApi(cookies.recotech_mapapps_token, removeCookie);
      setMyPlaces(myPlaces);

      placesBounds = placesBounds.map(place => {
        let updatedPlace = { ...place };
      
        // placesBoundsとmyPlacesで重複しているマーカーに対して、isOwnerをtrueに設定
        if (place.id !== null && myPlaces.includes(place.id)) {
          updatedPlace = { ...updatedPlace, isOwner: true, iconUrl: './assets/pin_nomal_owner.png' };
        }
        return updatedPlace;
      });
    }
    setMarkers(placesBounds);
    setInitialMarkers(placesBounds);
  };

  //MapのLoadが完了したら現在地をセットする
  useEffect(() => {
    if(isLoaded){
      getCurrentPositionAndMoveCenter()
    }
  }, [isLoaded]);

  //現在地を取得したらマーカーをセットする
  useEffect(() => {
    if(isCenterLoaded){
      window.scrollTo(0, 0);
      fetchData();
    }
  }, [isCenterLoaded]);

  //Marker配列が全て入るようにMapを調整する
  const panToAnyMarker = (markers:MakerData[],map:google.maps.Map | null) => {
    if (map && markers.length > 0) {
      bounds.current = new window.google.maps.LatLngBounds();
      markers.forEach(( marker ) => bounds.current!.extend({ lat: marker.latitude, lng: marker.longitude}));
      bounds.current!.extend({ lat: currentPosition.lat, lng: currentPosition.lng}); //現在地もセットする
      map.fitBounds(bounds.current!);
    }
  };

  //Cardをクリックした時の処理
  const handleCardClick = ( async (targetMarker:MakerData) => {
    //検索条件にマッチしたマーカーを取得
    let searchMarkerData = initialMarkers.filter( function( marker ) {
      return isIncludes(categoryNames,marker.categoryNames) ;
    })
    if(searchMarkerData.length === 0){ 
      searchMarkerData = [...initialMarkers]
    }
    //近い順に並び替えを行う
    sortMarkersAtCenter(searchMarkerData,currentPosition)

    //選択した回収所はリストから削除
    const withouttargetMarkerData = searchMarkerData.filter( function( marker ) {
    return marker.id !== targetMarker.id ;
    })

    setRelevantMarkers([])
    setMarker(targetMarker)
    const pointInfo = await getPlacePointsApi(targetMarker.id!,setAlertsMessage,setSeverity,setOpenSnackbar,setIsWaitingResponse);
    if(pointInfo) setPoint(pointInfo.amount)
    const statisticsInfo = await getPlaceStatisticsApi(targetMarker.id!,setAlertsMessage,setSeverity,setOpenSnackbar);
    if(statisticsInfo){
      setAmount(statisticsInfo.total_amount_sum)
      setEmission(statisticsInfo.reduction_carbon_emission_sum)
    }
    
    withouttargetMarkerData.map((marker) => {
    setRelevantMarkers((currentMarkers:MakerData[]) => [
        ...currentMarkers,{...marker}, //useRecoilStateで取得したobjctはreadonlyになっているため、スプレッド構文で新規に作り直す
    ]);
    });
    
    if (bottomDrawerRef.current) {
    bottomDrawerRef.current.scrollTop = 0;
    }
    setOpenBottomDrawer(true)
  })

  //GoogleMapが読み込まれていない場合はローディング画面を表示する
  if (!isLoaded || !isCenterLoaded){
    return (
      <LoadingBox
        uaParser={uaParser!}
        isGpsOn={isGpsOn}
      />
    );
  }

  return (
    <>
      {isWaitingResponse ?
        <Backdrop open={isWaitingResponse} sx={{zIndex:10000}}>
        <CircularProgress color='inherit' />
      </Backdrop>
      :<></>
      }
      <BottomDrawer
        openBottomDrawer={openBottomDrawer}
        setOpenBottomDrawer={setOpenBottomDrawer}
        bottomDrawerRef={bottomDrawerRef}
        handleBackClick={handleBackClick}
        handleCardClick={handleCardClick}
      />
      <AlertsDialog
        openDialog={openEditDialog}
        setOpenDialog={setOpenEditDialog}
        handleAgreeClick={()=>handleEditAgreeClick()}
        dialogTitle={t("pages.map.registerPoint")}
        dialogMessage={t("pages.map.confirmRegisterHere")}
        isWaitingResponse={isWaitingResponse}
      />
      <AlertsDialog
        openDialog={openCheckinDialog}
        setOpenDialog={setOpenCheckinDialog}
        handleAgreeClick={()=>handleCheckinAgreeClick()}
        dialogTitle={t("pages.map.registerResource")}
        dialogMessage={t("pages.map.confirmRegisterResource", { name: marker?.name })}
        isWaitingResponse={isWaitingResponse}
      />
      <AlertsSnackbar
        openSnackbar={openSnackbar}
        setOpenSnackbar={setOpenSnackbar}
        vertical={"bottom"}
        severity={severity}
        message={alertsMessage}
      />
      <GoogleMap
        mapContainerStyle={{
          width: "100vw",
          height: "90vh",
        }}
        center={initialPosition}
        zoom={zoom}
        onClick={handleMapClick}
        options={{
          zoomControl: false, // ズームコントロールを非表示にする
          streetViewControl: false, // ストリートビューコントロールを非表示にする
          fullscreenControl: false, // フルスクリーンコントロールを非表示にする
          mapTypeControl: false, // マップタイプコントロールを非表示にする
          scaleControl: false, // スケールコントロールを非表示にする
          rotateControl: false, // 回転コントロールを非表示にする
          panControl: false, // パンコントロールを非表示にする
          gestureHandling: 'greedy', // ジェスチャー操作の挙動を設定する
        }}
        onUnmount={onUnmount}
        onLoad={onLoad}
        onMouseDown={handleMapMouseDown}
        onMouseUp={handleMapMouseUp}
      >
        {markers.map((marker, index) => (
          <Marker
            onClick={() => {
              handleMarkerClick(marker,index)
            }}
            key={index}
            position={{ lat: marker.latitude, lng: marker.longitude}}
            icon={{
              url: (require(`${marker.iconUrl}`)),
              fillColor: '#EB00FF',
              scaledSize: new window.google.maps.Size(50, 50),
            }}
            />
        ))}
        <Marker
          key={`current-marker`}
          position={{ lat: currentPosition.lat, lng: currentPosition.lng}}
          icon={{
            url: (require('./assets/current.gif')),
            fillColor: '#EB00FF',
            scaledSize: new window.google.maps.Size(20, 20),
          }}
        />
        {registerPosition && (
          <Marker
            onClick={handleTemporaryMarkerClick}
            position={{ lat: registerPosition.lat, lng: registerPosition.lng }}
          />
        )}

      </GoogleMap>
      {(categoryNames.length > 0)?
      <Box
        display="flex"
        alignItems="start"
        justifyContent="start"
        flexDirection="row"
        sx={{position: "absolute" ,top: "2.5%",right: "3.3%",zIndex: 1301}}
      >
        <Box
            display="flex"
            alignItems="center"
            justifyContent="start"
            flexDirection="row"

        >
            <Search
              fontSize="large"
              onClick={() => {
                handleSearchClick(categoryNames,initialMarkers)
                setOpenSelect(false)
              }}
            ></Search>
            <ClearIcon
              fontSize="small"
              onClick={() => {
                handleClearClick(initialMarkers) 
                setOpenSelect(false)
              }}
            ></ClearIcon>
        </Box>
      </Box>:<></>
      }
      <Box
        display="flex"
        alignItems="start"
        justifyContent="start"
        flexDirection="row"
        sx={{position: "absolute" ,top: "1%",left: "0%",right: "0%"}}
      >
        <FormControl sx={{width: "100vw" }}>
          <MapMultiSelect
            categoryNames={categoryNames}
            setCategoryNames={setCategoryNames}
            categoryNameAll={categoryNameAll}
            openSelect={openSelect}
            setOpenSelect={setOpenSelect}
          />
        </FormControl>
      </Box>
      <Box
        display="flex"
        alignItems="end"
        flexDirection="column"
        sx={{position: "absolute" ,bottom: "10%",right: "5%"}}
      >
        <Fab size="small" sx={{color: "black", backgroundColor: "white"}}
          onClick={() => {
            handleFabClick()
          }}    
        >
          < NearMeIcon/>
        </Fab>
      </Box>
      <Box
        display="flex"
        alignItems="end"
        flexDirection="column"
        sx={{position: "absolute" ,bottom: "10%",left: "50%",transform:"translateX(-50%)"}}
      >
        {(marker!==null)?
          <OverviewCard
            key={`OverviewCard`}
            targetMarker={marker}
            handleCardClick={handleCardClick}
            myPlaces={myPlaces}
          />
        :<></>}
      </Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        width="100%"
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
        }}
      >
        <MyBottomNavigation />
      </Box>
    </>
  );
}

export default Map;